import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { inr, label, date } from "@/lib/fmt";
import { Loading, Empty } from "@/components/Bits";

function FeeRules() {
  const [rules, setRules] = useState(null);
  useEffect(() => { api.get("/fee-rules").then((r) => setRules(r.data)).catch(() => setRules(false)); }, []);
  if (rules === false) return <p className="text-sm text-stone-500 mt-6">Log in to see the current draft fee rules and fee previews.</p>;
  if (!rules) return null;
  return (
    <div className="card overflow-x-auto mt-8"><table className="tbl"><thead><tr><th>Fee</th><th>Payer</th><th>Basis</th><th>Min / max</th><th>Tax</th><th>Effective</th></tr></thead>
      <tbody>{rules.map((r) => <tr key={r.id}><td>{r.name} (v{r.version})</td><td>{label(r.payer)}</td><td>{r.basis === "percent" ? `${r.value}%` : inr(r.value)}</td><td>{inr(r.min_fee, "—")} / {inr(r.max_fee, "—")}</td><td>{r.tax_label} {r.tax_percent}%</td><td>{date(r.effective_from)}</td></tr>)}</tbody></table></div>
  );
}

export default function ContentPage() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  useEffect(() => { setP(null); api.get(`/content/${slug}`).then((r) => { setP(r.data); document.title = `${r.data.title} — VyapaarSetu`; }).catch(() => setP(false)); }, [slug]);
  if (p === null) return <Loading />;
  if (!p) return <div className="max-w-3xl mx-auto p-8"><Empty title="Page not found" /></div>;
  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid={`content-page-${slug}`}>
      {p.status === "draft" && ["privacy", "terms", "disclosure"].includes(slug) && <p className="chip bg-[#F5E7C8] text-[#7A5A12] mb-4" data-testid="draft-policy-label">Draft — pending operator review</p>}
      <h1 className="text-3xl sm:text-5xl text-[#29483D]">{p.title}</h1>
      <div className="mt-8 space-y-4 text-[15px] leading-relaxed text-[#292A25]">{p.body.split("\n").map((line, i) => (line.trim() ? <p key={i}>{line}</p> : <div key={i} className="h-2" />))}</div>
      {slug === "fees" && <FeeRules />}
      <p className="text-xs text-stone-500 mt-10">Last updated {date(p.updated_at)}</p>
    </article>
  );
}

export function InstallPage() {
  const [evt, setEvt] = useState(null);
  const [installed, setInstalled] = useState(window.matchMedia?.("(display-mode: standalone)").matches);
  useEffect(() => {
    const h = (e) => { e.preventDefault(); setEvt(e); };
    window.addEventListener("beforeinstallprompt", h);
    window.addEventListener("appinstalled", () => setInstalled(true));
    return () => window.removeEventListener("beforeinstallprompt", h);
  }, []);
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid="install-page">
      <p className="eyebrow">Installable web app</p>
      <h1 className="text-3xl sm:text-5xl text-[#29483D] mt-2">Install VyapaarSetu</h1>
      <p className="mt-4 text-stone-600">VyapaarSetu is an installable web app — not an App Store or Play Store app. Installing adds an icon and opens it in its own window. Only the public shell is cached offline; messages, documents and dashboards always need a connection.</p>
      {installed ? <p className="card p-4 mt-6 text-[#29483D]" data-testid="install-status">VyapaarSetu is already running as an installed app.</p> :
        evt ? <button className="btn-primary mt-6" onClick={async () => { evt.prompt(); await evt.userChoice; setEvt(null); }} data-testid="install-btn">Install app</button> :
          <p className="text-sm text-stone-500 mt-6" data-testid="install-status">Your browser hasn't offered one-tap install here. Use the steps below.</p>}
      <div className="grid sm:grid-cols-2 gap-4 mt-8">
        <div className="card p-5"><h2 className="font-display text-lg text-[#29483D]">iPhone / iPad (Safari)</h2><ol className="list-decimal ml-5 mt-2 text-sm space-y-1"><li>Open this site in Safari.</li><li>Tap the Share button.</li><li>Choose <b>Add to Home Screen</b>.</li><li>Tap <b>Add</b>.</li></ol></div>
        <div className="card p-5"><h2 className="font-display text-lg text-[#29483D]">Android (Chrome)</h2><ol className="list-decimal ml-5 mt-2 text-sm space-y-1"><li>Open the browser menu (⋮).</li><li>Tap <b>Install app</b> or <b>Add to Home screen</b>.</li></ol></div>
        <div className="card p-5 sm:col-span-2"><h2 className="font-display text-lg text-[#29483D]">Desktop (Chrome / Edge)</h2><p className="text-sm mt-2">Click the install icon in the address bar, or open the browser menu and choose <b>Install VyapaarSetu</b>. Firefox and desktop Safari may not support installation; the site works normally in the browser.</p></div>
      </div>
    </div>
  );
}
